import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import TargetWidget from '../components/TargetWidget';
import { fmtDate } from '../utils/dates';

const getToken = () => localStorage.getItem('token');

const STATUS_META = {
  Rejected:   { color: 'bg-rose-100 text-rose-700 border-rose-300',     dot: 'bg-rose-500'   },
  'No Hope':  { color: 'bg-orange-100 text-orange-700 border-orange-300', dot: 'bg-orange-400' },
  Progressing:{ color: 'bg-blue-100 text-blue-700 border-blue-300',      dot: 'bg-blue-500'   },
  Converted:  { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-xs text-gray-300 font-semibold">— Pending —</span>;
  const m = STATUS_META[status] || STATUS_META.Rejected;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold ${m.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {status}
    </span>
  );
};

const STATUSES = ['Rejected', 'No Hope', 'Progressing', 'Converted'];

const TelecallerDashboard = ({ name, API_URL: propApiUrl }) => {
  const API_URL = propApiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('All');
  const [updating, setUpdating] = useState(null);
  const [feedback, setFeedback] = useState({});

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/telecaller/leads`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setLeads(r.data || []);
    } catch { setLeads([]); } finally { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, telecaller_status) => {
    setUpdating(id);
    setFeedback(p => ({ ...p, [id]: '' }));
    try {
      const r = await axios.put(
        `${API_URL}/api/telecaller/leads/${id}/status`,
        { telecaller_status },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setLeads(p => p.map(l => l.id === id ? r.data.lead : l));
      setFeedback(p => ({ ...p, [id]: 'saved' }));
      setTimeout(() => setFeedback(p => ({ ...p, [id]: '' })), 2000);
    } catch {
      setFeedback(p => ({ ...p, [id]: 'error' }));
    } finally { setUpdating(null); }
  };

  const counts = {
    All: leads.length,
    Pending: leads.filter(l => !l.telecaller_status).length,
    Progressing: leads.filter(l => l.telecaller_status === 'Progressing').length,
    Converted: leads.filter(l => l.telecaller_status === 'Converted').length,
    Rejected: leads.filter(l => l.telecaller_status === 'Rejected').length,
    'No Hope': leads.filter(l => l.telecaller_status === 'No Hope').length,
  };

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || l.name?.toLowerCase().includes(q) || l.contact_number?.includes(q) || l.district?.toLowerCase().includes(q);
    const matchFilter = filter === 'All' || (filter === 'Pending' ? !l.telecaller_status : l.telecaller_status === filter);
    return matchSearch && matchFilter;
  });

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCalls = leads.filter(l => l.updated_at?.slice(0, 10) === todayStr && l.telecaller_status).length;
  const converted  = leads.filter(l => l.telecaller_status === 'Converted').length;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(250,204,21,0.10),transparent_22%),linear-gradient(180deg,#f9fafb_0%,#f3f4f6_100%)] px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1920px] overflow-hidden rounded-[32px] border border-gray-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.08)]">

        {/* Header */}
        <div className="bg-[radial-gradient(circle_at_top_right,rgba(250,204,21,0.18),transparent_30%),linear-gradient(135deg,#0a0a0a_0%,#1a1a1a_60%,#111_100%)] px-5 py-6 text-white md:px-8 md:py-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-yellow-400">Telecaller Dashboard</p>
              <h2 className="mt-1.5 text-2xl font-black">{name || 'Telecaller'}</h2>
              <p className="mt-1 text-sm text-white/55">Call leads, update status and drive conversions.</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 backdrop-blur-sm">
              <span className="text-xl">📞</span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-yellow-400">Role</p>
                <p className="text-sm font-extrabold text-white">Telecaller</p>
              </div>
            </div>
          </div>

          {/* Stat tiles */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Total Assigned', value: leads.length },
              { label: 'Converted',      value: converted },
              { label: "Today's Updates",value: todayCalls },
              { label: 'Pending',        value: counts.Pending },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3.5 backdrop-blur-sm">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-yellow-300">{label}</p>
                <p className="mt-1.5 text-2xl font-black text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 py-5 md:px-6">
          <TargetWidget API_URL={API_URL} />
          {/* Filter + Search */}}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <input type="text" placeholder="Search name, contact, district…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pl-10 text-sm shadow-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100" />
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['All','Pending','Progressing','Converted','No Hope','Rejected'].map(f => (
                <button key={f} type="button" onClick={() => setFilter(f)}
                  className={`rounded-[18px] border px-3.5 py-1.5 text-xs font-extrabold transition ${
                    filter === f
                      ? 'border-black bg-black text-yellow-300 shadow-[0_4px_14px_rgba(0,0,0,0.2)]'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-yellow-400'
                  }`}>
                  {f} <span className="ml-1 opacity-60">({counts[f] ?? 0})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Leads */}
          {loading ? (
            <div className="flex items-center justify-center py-24 text-sm font-semibold text-gray-400">
              <span className="mr-2 animate-spin text-xl">⟳</span> Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-gray-300 bg-gray-50/60 py-24 text-center">
              <p className="text-4xl">📞</p>
              <p className="mt-3 text-base font-black text-gray-500">{search || filter !== 'All' ? 'No leads match filters.' : 'No leads assigned to you yet.'}</p>
              <p className="mt-1 text-sm text-gray-400">Leads forwarded by agents will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[24px] border border-gray-200 bg-white shadow-[0_8px_32px_rgba(15,23,42,0.06)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#111] text-left">
                    {['#','Date','Name','District','Contact','WhatsApp','Source','Forwarded On','Status','Update'].map(h => (
                      <th key={h} className="whitespace-nowrap px-4 py-3.5 text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr key={l.id} className={`border-b transition-colors hover:bg-yellow-50/50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}>
                      <td className="px-4 py-3 text-xs font-bold text-gray-400">{i + 1}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-400">{l.date_of_lead || '-'}</td>
                      <td className="px-4 py-3 font-extrabold text-gray-900">{l.name}</td>
                      <td className="px-4 py-3 text-gray-500">{l.district || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <a href={`tel:${l.contact_number}`}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-black bg-black px-3 py-1.5 text-[11px] font-extrabold text-yellow-300 hover:bg-[#222] transition">
                          📞 {l.contact_number}
                        </a>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {l.whatsapp_number
                          ? <a href={`https://wa.me/91${l.whatsapp_number}`} target="_blank" rel="noreferrer" className="font-bold text-emerald-600 hover:underline">{l.whatsapp_number}</a>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{l.leads_through || '-'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">
                        {l.forwarded_to_telecaller_at ? fmtDate(l.forwarded_to_telecaller_at) : '-'}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={l.telecaller_status} /></td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={l.telecaller_status || ''}
                            onChange={e => e.target.value && updateStatus(l.id, e.target.value)}
                            disabled={updating === l.id}
                            className="rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-extrabold text-gray-700 outline-none focus:border-yellow-400 disabled:opacity-50">
                            <option value="">-- Set Status --</option>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          {feedback[l.id] === 'saved' && <span className="text-xs font-bold text-emerald-600">✓</span>}
                          {feedback[l.id] === 'error' && <span className="text-xs font-bold text-rose-600">!</span>}
                          {updating === l.id && <span className="text-xs font-bold text-gray-400 animate-spin">⟳</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelecallerDashboard;
